import { useAppData } from "eddev/hooks"
import { AwardSeasonPageFragment } from "../../../types.graphql"
import { useIsLoggedIn, useMembershipStatus } from "@features/accounts/hooks/useAuth"
import React, { ReactNode } from "react"
import { Button } from "@components/controls/Button"
import { useAwardForceTransfer } from "@hooks/queries"
import { LoadingResults } from "@components/layout/LoadingResults"
import { IconCaretRight } from "@components/svg/Icons"
import { MakeURL } from "@utils/create-url"
import { Link } from "eddev/routing"
import { displayAlert } from "@hooks/useAlertOverlay"

export function StartAwardsSubmission() {
  const latestAward = useAppData((data) => data.latestAward)
  const loggedIn = useIsLoggedIn()
  const membership = useMembershipStatus()
  const loginLink = useAwardForceTransfer()

  let heading = ""
  let cards: ReactNode[] = []
  let secondaryLink: ReactNode = null

  if (latestAward?.info?.status === "open") {
    heading = "Entries are open"
    if (loggedIn && membership.loading && !env.admin) {
      return (
        <div className="col-span-12 md:col-3-6 mb-8">
          <LoadingResults />
        </div>
      )
    }
    if (loggedIn && !env.admin) {
      cards.push(
        <Card
          key="start-submission"
          title={`Manage your entries for the ${latestAward.info.awardYear} AGDA Awards`}
          subtitle={"Submit your entries"}
          buttons={
            <Button
              onClick={() => {
                loginLink.mutateAsync(
                  { input: {} },
                  {
                    onSuccess(data) {
                      const url = data.awardForceTransfer?.token!
                      window.location.href = url
                    },
                    onError() {
                      displayAlert({
                        title: "An error occurred",
                        message: "Something went wrong while trying to transfer you to the submmissions portal. Please try again.",
                      })
                    },
                  },
                )
              }}
              loading={loginLink.isPending}
              rightIcon={<IconCaretRight />}
            >
              Entries Portal
            </Button>
          }
        />,
      )
    }
    if (!loggedIn || env.admin) {
      cards.push(
        <Card
          key="login"
          title={`Sign in to start your ${latestAward.info.awardYear} AGDA Awards submission`}
          subtitle={"Already a member?"}
          buttons={
            <Button href={MakeURL.loginFirst("/awards/")} rightIcon={<IconCaretRight />}>
              Sign In
            </Button>
          }
        />,
      )
      cards.push(
        <Card
          key="become-a-member"
          subtitle={"Not a member?"}
          title={"You can still enter the AGDA Awards without a membership - simply create a non-member account to get started."}
          buttons={
            <>
              <Button href={"/join/?reason=awards"} rightIcon={<IconCaretRight />}>
                Create Account
              </Button>
            </>
          }
        />,
      )
    }
  } else {
    return null
  }

  return (
    <div className="col-span-12 md:col-3-6 mb-8">
      {heading && <h2 className="type-title-m-medium mb-3">{heading}</h2>}
      <div className="flex flex-wrap gap-x-2 gap-y-4">{cards}</div>
      {secondaryLink}
    </div>
  )
}

type CardProps = {
  title?: string
  subtitle?: string
  buttons?: ReactNode
}

function Card(props: CardProps) {
  return (
    <div className="bg-grey-light border-t px-4 pt-3 pb-4 text-black stack-y-6 justify-between min-w-[300px] flex-1">
      <div className="stack-y-2 text-balance">
        <div className="type-ui-s-roman-caps">{props.subtitle}</div>
        <div className="type-title-s-medium max-w-[20em]">{props.title}</div>
      </div>
      {props.buttons && <div className="flex flex-wrap gap-2">{props.buttons}</div>}
    </div>
  )
}
